import {
  ChangeDetectionStrategy, Component, EventEmitter, HostListener,
  Input, Output, computed, signal,
} from '@angular/core';
import {
  ConversationTurn, FeedbackCategory, FeedbackService,
} from '../../services/feedback.service';

interface CategoryOption {
  id: FeedbackCategory;
  label: string;
}

/**
 * Send Feedback dialog — implements the Agent Hub design (Agent Hub.dc.html).
 * Self-contained two-state modal (form → sent). The parent supplies the user +
 * conversation context and toggles visibility; this component owns the form
 * state and the POST via FeedbackService, and emits (close) when dismissed.
 */
@Component({
  selector: 'app-send-feedback-dialog',
  standalone: true,
  templateUrl: './send-feedback-dialog.component.html',
  styleUrls: ['./send-feedback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendFeedbackDialogComponent {
  /** Display name of the signed-in user (shown to support in the email). */
  @Input() userName = '';
  /** Email/account of the signed-in user. */
  @Input() userEmail = '';
  /** Current conversation transcript; attached only when the checkbox is on. */
  @Input() conversation: ConversationTurn[] = [];
  @Input() sessionId: string | null = null;
  @Input() agentId: string | null = null;
  @Input() agentName: string | null = null;

  @Output() readonly close = new EventEmitter<void>();

  readonly categories: CategoryOption[] = [
    { id: 'bug', label: 'Bug' },
    { id: 'idea', label: 'Idea' },
    { id: 'agent', label: 'Agent quality' },
    { id: 'other', label: 'Other' },
  ];

  readonly category = signal<FeedbackCategory>('idea');
  readonly message = signal<string>('');
  /** Chat context is strictly opt-in: nothing is attached unless the user
   *  turns it on, so general feedback never silently ships a transcript. */
  readonly includeConversation = signal<boolean>(false);
  /** Whether the "what will be shared" transcript preview is expanded. */
  readonly previewOpen = signal<boolean>(false);

  readonly submitting = signal<boolean>(false);
  readonly sent = signal<boolean>(false);
  readonly error = signal<string>('');

  readonly hasConversation = computed(() => (this.conversation?.length ?? 0) > 0);
  readonly canSubmit = computed(() => this.message().trim().length > 0 && !this.submitting());

  constructor(private readonly feedback: FeedbackService) {}

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.submitting()) this.onClose();
  }

  selectCategory(id: FeedbackCategory): void {
    this.category.set(id);
  }

  onMessageInput(event: Event): void {
    this.message.set((event.target as HTMLTextAreaElement).value);
  }

  toggleAttach(): void {
    this.includeConversation.update(v => !v);
    if (!this.includeConversation()) this.previewOpen.set(false);
  }

  togglePreview(): void {
    this.previewOpen.update(v => !v);
  }

  onClose(): void {
    this.close.emit();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    this.error.set('');

    const attach = this.includeConversation() && this.hasConversation();
    this.feedback
      .submitFeedback({
        category: this.category(),
        message: this.message().trim(),
        include_conversation: attach,
        conversation: attach ? this.conversation : undefined,
        session_id: this.sessionId,
        agent_id: this.agentId,
        agent_name: this.agentName,
      })
      .subscribe({
        next: () => {
          this.sent.set(true);
          this.submitting.set(false);
        },
        error: () => {
          this.error.set('Something went wrong sending your feedback. Please try again.');
          this.submitting.set(false);
        },
      });
  }
}
